import * as amplitude from './amplitude'
import * as google from './google'

export enum AnalyticsService {
  'Amplitude' = 'amplitude',
  'Google' = 'google',
}

export enum AnalyticsEventType {
  'Click' = 'click',
  'Navigate' = 'navigate',
  'NavigateExt' = 'navigateExt',
  'PageLoad' = 'pageLoad',
  'ModalLoad' = 'modalLoad',
  'Error' = 'error',
  'Select' = 'select',
  'Change' = 'change',
}

type CompanyFeaturesType = {
  allowSelfSignup?: boolean
  ats?: boolean
  branding?: boolean
  calendar?: boolean
  disableCandidateNotifications?: boolean
  excelExport?: boolean
  feedbackPulse?: boolean
  jobAdListings?: boolean
  live?: boolean
  reporting?: boolean
  sequenced?: boolean
  sms?: boolean
  speechToText?: boolean
  videoInterview?: boolean
}

type PermissionsType = {
  account?: boolean
  admin?: boolean
  billing?: boolean
  calendar?: boolean
  createInterviews?: boolean
  createJobPositions?: boolean
  editUsers?: boolean
  reporting?: boolean
  seeAllPositions?: boolean
}

export interface UserProperties {
  companyId?: string
  companyName?: string
  companyDomain?: string
  companyFeatures?: CompanyFeaturesType
  companyCreatedAt?: string
  companySubscription?: string
  permissions?: PermissionsType
  createdAt?: string
}

export interface AnalyticsProperties {
  route: string // current route (e.g. /admin/interview/:interviewId/candidates)
  routeParams?: Record<string, string> // route params (e.g. {interviewId: 123abc456efg})
  [key: string]: unknown // event properties (other than route)
}

export interface AnalyticsEvent {
  locationId: string // current location (e.g. "candidatePage", "topNav")
  id: string // unique event identifier (e.g. "inviteButton", "logoutButton")
  properties: Partial<AnalyticsProperties>
}

const formatEventId = (
  event: AnalyticsEvent,
  eventType: AnalyticsEventType
) => {
  return `${event.locationId}.${event.id}.${eventType}`
}

export const initAnalytics = (enabledAnalytics: AnalyticsService[]) => {
  if (enabledAnalytics.includes(AnalyticsService.Amplitude)) {
    amplitude.init()
  }
  if (enabledAnalytics.includes(AnalyticsService.Google)) {
    google.init()
  }
}

export const disableAnalytics = (disabledAnalytics: AnalyticsService[]) => {
  if (disabledAnalytics.includes(AnalyticsService.Amplitude)) {
    amplitude.disable()
  }
  if (disabledAnalytics.includes(AnalyticsService.Google)) {
    google.disable()
  }
}

export const onLogin = (userId: string, userProperties: UserProperties) => {
  amplitude.login(userId, userProperties)
  google.login(userId) // TODO: REMOVE THIS IF NOT NEEDED
}

export const onLogout = () => {
  amplitude.logout()
  google.logout() // TODO: remove if not needed
}

export const onClickEvent = (event: AnalyticsEvent) => {
  amplitude.logEvent(
    formatEventId(event, AnalyticsEventType.Click),
    event.properties
  )
}

export const onNavigateEvent = (event: AnalyticsEvent) => {
  amplitude.logEvent(
    formatEventId(event, AnalyticsEventType.Navigate),
    event.properties
  )
}

export const onNavigateExternalEvent = (event: AnalyticsEvent) => {
  amplitude.logEvent(
    formatEventId(event, AnalyticsEventType.NavigateExt),
    event.properties
  )
}

export const onPageLoadEvent = (properties: AnalyticsProperties) => {
  amplitude.logEvent(AnalyticsEventType.PageLoad, {
    ...properties,
  })
  google.trackPageView(properties.route)
}

export const onModalLoadEvent = (event: AnalyticsEvent) => {
  const eventId = formatEventId(event, AnalyticsEventType.ModalLoad)
  amplitude.logEvent(eventId, event.properties)
  google.trackModalView(eventId)
}

export const onErrorEvent = (event: AnalyticsEvent) => {
  amplitude.logEvent(
    formatEventId(event, AnalyticsEventType.Error),
    event.properties
  )
}

export const onSelectEvent = (event: AnalyticsEvent) => {
  amplitude.logEvent(
    formatEventId(event, AnalyticsEventType.Select),
    event.properties
  )
}

export const onChangeEvent = (event: AnalyticsEvent) => {
  amplitude.logEvent(
    formatEventId(event, AnalyticsEventType.Change),
    event.properties
  )
}
